// App.js
import React, { useState, useEffect, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import LandingPage from "./components/LandingPage";
import Dashboard from "./components/Dashboard";
import { jwtDecode } from "jwt-decode";
import "./App.css";
import AuthCallback from "./components/AuthCallback";
import SignOutCallback from "./components/SignOutCallback";
import { logout as oidcLogout } from "./services/authService";

// Google client ID from environment variables (set in .env file)
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function App() {
  const clearLocalSession = () => {
    // Remove auth data from localStorage
    localStorage.removeItem("authToken");
    localStorage.removeItem("userInfo");
    localStorage.removeItem("idToken");
    localStorage.removeItem("oidcIdToken");

    // Clear any Google auth cookies
    const googleCookies = [
      "g_state",
      "g_auth",
      "gid_aps",
      "gid_auth",
      "g_csrf_token",
    ];
    googleCookies.forEach((cookieName) => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });

    // Clear Google One Tap state
    if (window.google && window.google.accounts && window.google.accounts.id) {
      window.google.accounts.id.disableAutoSelect();
    }

    // Update app state
    setIsAuthenticated(false);
    setUserInfo(null);
  };
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check authentication status when app loads
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const userData = localStorage.getItem("userInfo");

    if (token) {
      try {
        // Decode the JWT token to check if it's still valid
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000; // Current time in seconds

        if (decodedToken.exp && decodedToken.exp > currentTime) {
          // Token is valid, update state with user info
          setIsAuthenticated(true);
          setUserInfo(userData ? JSON.parse(userData) : null);
        } else {
          // Token expired, clear storage
          localStorage.removeItem("authToken");
          localStorage.removeItem("userInfo");
        }
      } catch (error) {
        console.error("Invalid token:", error);
        localStorage.removeItem("authToken");
        localStorage.removeItem("userInfo");
      }
    }
    // Done checking auth status
    setLoading(false);
  }, []);

  /**
   * Callback to handle authentication results from the backend
   * Called by the AuthCallback component after OAuth redirect
   * Memoized so AuthCallback's effect does not re-fire on every parent render
   * (which would repeat the code→token exchange and break single-use codes).
   */
  const handleAuthCallback = useCallback((searchParams) => {
    const token = searchParams.get("token");
    const userData = searchParams.get("userData");

    if (token) {
      localStorage.setItem("authToken", token);

      if (userData) {
        try {
          // URLSearchParams.get() already decodes; try parse first, then decode if needed
          let parsedUserData;
          try {
            parsedUserData = JSON.parse(userData);
          } catch {
            parsedUserData = JSON.parse(decodeURIComponent(userData));
          }
          localStorage.setItem("userInfo", JSON.stringify(parsedUserData));
          setUserInfo(parsedUserData);
        } catch (error) {
          console.error("Error parsing user data:", error);
        }
      }
      setIsAuthenticated(true);
      return true;
    }

    return false;
  }, []);

  /**
   * Handles user logout
   * Clears tokens, user data, and resets Google login state
   */

  const handleLogout = () => {
    // // Remove auth data from localStorage
    // localStorage.removeItem("authToken");
    // localStorage.removeItem("userInfo");

    // // Clear any Google auth cookies
    // const googleCookies = ['g_state', 'g_auth', 'gid_aps', 'gid_auth', 'g_csrf_token'];
    // googleCookies.forEach(cookieName => {
    //   document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    // });

    // // Clear Google One Tap state
    // if (window.google && window.google.accounts && window.google.accounts.id) {
    //   window.google.accounts.id.disableAutoSelect();
    // }

    // // Update state
    // setIsAuthenticated(false);
    // setUserInfo(null);

    // // Force page reload to ensure clean state
    // window.location.href = '/';
    const oidcIdToken = localStorage.getItem("oidcIdToken");
    clearLocalSession();

    oidcLogout(oidcIdToken).catch((error) => {
      console.error("OIDC signout redirect failed:", error);
      window.location.href = "/";
    });
  };

  if (loading) {
    return <div className="loading-container">Loading...</div>;
  }

  return (
    // GoogleOAuthProvider allows use of Google login across the app
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <Router>
        <div className="App">
          <Routes>
            <Route
              path="/"
              element={
                isAuthenticated ? <Navigate to="/dashboard" /> : <LandingPage />
              }
            />

            {/* Dashboard route: accessible only if authenticated */}
            <Route
              path="/dashboard"
              element={
                isAuthenticated ? (
                  <Dashboard user={userInfo} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/" />
                )
              }
            />

            {/* OAuth Callback route: processes auth tokens returned from backend */}
            <Route
              path="/auth-callback"
              element={
                <AuthCallback
                  handleAuthCallback={handleAuthCallback}
                  isAuthenticated={isAuthenticated}
                />
              }
            />
            <Route
              path="/signout-callback"
              element={<SignOutCallback onComplete={clearLocalSession} />}
            />
          </Routes>
        </div>
      </Router>
    </GoogleOAuthProvider>
  );
}

export default App;
